import { useQuery } from "@apollo/client/react";
import { createContext, type ReactNode, use, useMemo } from "react";
import {
  GetMeasurementUnitsDocument,
  type GetMeasurementUnitsQuery,
  type RecipeDetailFragment,
} from "../../gql/generated/graphqlSchema.tsx";

type MeasurementUnit = GetMeasurementUnitsQuery["measurementUnits"][number];

type RecipeContextValue = {
  readonly recipe: RecipeDetailFragment;
  readonly setRecipe: (recipe: RecipeDetailFragment | undefined | null) => void;
  readonly refetch: () => Promise<unknown>;
  readonly units: readonly MeasurementUnit[];
  readonly readOnly: boolean;
};

const RecipeContext = createContext<RecipeContextValue | null>(null);

type RecipeProviderProps = Omit<RecipeContextValue, "units" | "readOnly"> & {
  readonly readOnly?: boolean;
  readonly children: ReactNode;
};

export const RecipeProvider = ({ recipe, setRecipe, refetch, readOnly = false, children }: RecipeProviderProps) => {
  const { data: unitsData } = useQuery(GetMeasurementUnitsDocument);
  const effectiveReadOnly = readOnly || recipe.deleted;
  const value = useMemo(
    () => ({ recipe, setRecipe, refetch, units: unitsData?.measurementUnits ?? [], readOnly: effectiveReadOnly }),
    [recipe, setRecipe, refetch, unitsData, effectiveReadOnly],
  );
  return <RecipeContext value={value}>{children}</RecipeContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRecipe = () => {
  const context = use(RecipeContext);
  if (!context) throw new Error("useRecipe must be used within a RecipeProvider");
  return context;
};
